import React, { useState, useEffect } from 'react';
import InlineProductEdit from './InlineProductEdit';
import { warehousePricingService } from '../../services/warehousePricingService';
import warehousesService from '../../services/warehousesService';
import { useMarginCalculation } from '../../utils/marginCalculator';
import { FaHistory } from 'react-icons/fa';
import ActionMenu from '../common/ActionMenu';

const InventoryTable = ({
  products = [],
  onUpdateStock,
  onEditProduct,
  onSaveProduct,
  onShowHistory,
  loading = false,
  pagination = {},
  onPageChange = () => {},
  currentView = 'all',
  canEdit = true
}) => {
  const [editingStock, setEditingStock] = useState({});
  const [tempValues, setTempValues] = useState({});
  const [editingProduct, setEditingProduct] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationPrices, setLocationPrices] = useState([]);
  const [productMargins, setProductMargins] = useState({});
  
  // Stany sortowania
  const [sortColumn, setSortColumn] = useState('nazwa');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Hook do centralnego obliczania marży
  const { calculateMargin } = useMarginCalculation();

  // Pobierz domyślny magazyn przy pierwszym renderowaniu
  useEffect(() => {
    const loadDefaultWarehouse = async () => {
      try {
        // Pobierz liste magazynow z warehousesService
        const result = await warehousesService.getWarehousesWithStatus();
        if (result.success && result.data && result.data.length > 0) {
          setCurrentLocation(result.data[0]); // używamy tego samego state dla kompatybilności
        }
      } catch (error) {
        console.error('Błąd pobierania magazynów:', error);
      }
    };
    
    loadDefaultWarehouse();
  }, []);

  // Pobierz ceny magazynowe gdy magazyn zostanie ustawiony
  useEffect(() => {
    if (currentLocation) {
      loadWarehousePrices();
    }
  }, [currentLocation]);

  // Pobierz marże z centralnego API gdy produkty lub ceny magazynowe się zmienią
  // Używamy ref aby uniknąć wielokrotnych wywołań
  const lastMarginsHashRef = React.useRef('');
  useEffect(() => {
    // Utwórz hash z ID produktów i liczby cen żeby sprawdzić czy coś się zmieniło
    const marginsHash = `${products.map(p => p.id).join(',')}_${locationPrices.length}`;
    if (marginsHash !== lastMarginsHashRef.current && products.length > 0) {
      lastMarginsHashRef.current = marginsHash;
      loadMarginsForProducts();
    }
  }, [products, locationPrices]);

  const loadWarehousePrices = async () => {
    if (!currentLocation) return;
    
    try {
      const response = await warehousePricingService.getWarehousePrices(currentLocation.id);
      // API zwraca { success: true, data: { prices: [...] } }
      const prices = response.data?.prices || response.data || [];
      setLocationPrices(prices);
    } catch (error) {
      console.error('Błąd pobierania cen magazynowych:', error);
      setLocationPrices([]);
    }
  };

  // Pobierz marże dla wszystkich produktów z centralnego API
  const loadMarginsForProducts = async () => {
    if (!products || products.length === 0) return;
    
    const margins = {};
    const promises = products.map(async (product) => {
      try {
        const marginData = getMarginCalculationData(product);
        
        // Jeśli mamy marżę bezpośrednio z API, użyj jej
        if (marginData.marginFromAPI !== null && marginData.marginFromAPI !== undefined) {
          const marginAmount = marginData.sellPriceNetto - marginData.buyPriceNetto;
          margins[product.id] = {
            margin_percent: marginData.marginFromAPI,
            margin_amount: marginAmount,
            markup_percent: marginData.buyPriceNetto > 0 ? 
              ((marginAmount / marginData.buyPriceNetto) * 100) : 0,
            profit_amount: marginAmount,
            source: 'api'
          };
        } else if (marginData.sellPriceNetto > 0 && marginData.buyPriceNetto > 0) {
          // Fallback - oblicz marżę lokalnie
          const margin = await calculateMargin(
            marginData.sellPriceNetto,
            marginData.buyPriceNetto
          );
          margins[product.id] = { ...margin, source: 'calculated' };
        } else {
          margins[product.id] = {
            margin_percent: 0,
            margin_amount: 0,
            markup_percent: 0,
            profit_amount: 0,
            source: 'none'
          };
        }
      } catch (error) {
        console.error(`Błąd pobierania marży dla produktu ${product.id}:`, error);
        margins[product.id] = {
          margin_percent: 0,
          margin_amount: 0,
          markup_percent: 0,
          profit_amount: 0,
          error: error.message
        };
      }
    });
    
    await Promise.all(promises);
    setProductMargins(margins);
  };

  // Funkcja pomocnicza do przygotowania danych dla centralnego serwisu marży
  const getMarginCalculationData = (product) => {
    // Safety check: ensure locationPrices is an array
    const safePrices = Array.isArray(locationPrices) ? locationPrices : [];
    
    // Sprawdź czy produkt ma cenę specjalną dla aktualnego magazynu
    // API zwraca: warehouse_price_net (cena netto specjalna) i purchase_price
    const warehousePrice = safePrices.find(lp => lp.product_id === product.id);
    
    // Użyj ceny specjalnej z API (warehouse_price_net) jeśli istnieje
    const sellPriceNetto = warehousePrice?.warehouse_price_net || 
      product.warehouse_price_net || 
      product.price_net || 
      product.cena_sprzedazy_netto || 0;
    
    // Użyj ceny zakupu z API (purchase_price) - jest to cena_zakupu_netto
    const buyPriceNetto = warehousePrice?.purchase_price || 
      product.purchase_price || 
      product.cena_zakupu_netto || 0;
    
    // Użyj marży bezpośrednio z API jeśli jest dostępna
    const marginFromAPI = warehousePrice?.margin || null;
    
    return {
      sellPriceNetto,
      buyPriceNetto,
      hasSpecialPrice: warehousePrice?.has_special_price || false,
      marginFromAPI
    };
  };

  const handleStockEdit = (productId, currentStock) => {
    setEditingStock(prev => ({ ...prev, [productId]: true }));
    setTempValues(prev => ({ ...prev, [productId]: currentStock }));
  };

  const handleStockSave = async (productId) => {
    const newStock = tempValues[productId];
    if (newStock === undefined || newStock === null) return;

    const result = await onUpdateStock(productId, {
      operation: 'set',
      stock_quantity: parseFloat(newStock),
      reason: 'Korekta manualna przez interfejs'
    });

    if (result.success) {
      setEditingStock(prev => ({ ...prev, [productId]: false }));
      setTempValues(prev => {
        const updated = { ...prev };
        delete updated[productId];
        return updated;
      });
    }
  };

  const handleStockCancel = (productId) => {
    setEditingStock(prev => ({ ...prev, [productId]: false }));
    setTempValues(prev => {
      const updated = { ...prev };
      delete updated[productId];
      return updated;
    });
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
  };

  const handleSaveProductEdit = async (productId, formData) => {
    try {
      await onSaveProduct(productId, formData);
      setEditingProduct(null);
    } catch (error) {
      throw error;
    }
  };

  const getRowClass = (product) => {
    const stock = product.stock_quantity || 0;
    const minLevel = product.min_stock_level || 0;

    if (stock <= 0) {
      return 'table-danger';
    } else if (stock <= minLevel) {
      return 'table-warning';
    }
    return '';
  };

  // Funkcja obsługi sortowania
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Ikona sortowania
  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <span style={{ opacity: 0.3, marginLeft: '3px' }}>↕</span>;
    }
    return <span style={{ marginLeft: '3px', color: 'var(--info)' }}>
      {sortDirection === 'asc' ? '↑' : '↓'}
    </span>;
  };

  // Sortowanie produktów
  const sortedProducts = [...products].sort((a, b) => {
    let valueA, valueB;
    
    switch (sortColumn) {
      case 'id':
        valueA = a.id || 0;
        valueB = b.id || 0;
        break;
      case 'nazwa':
        valueA = (a.nazwa || a.name || '').toLowerCase();
        valueB = (b.nazwa || b.name || '').toLowerCase();
        break;
      case 'kategoria':
        valueA = (a.kategoria || a.category || '').toLowerCase();
        valueB = (b.kategoria || b.category || '').toLowerCase();
        break;
      case 'cena_sprzedazy':
        valueA = parseFloat(a.cena_sprzedazy_brutto || a.price || 0);
        valueB = parseFloat(b.cena_sprzedazy_brutto || b.price || 0);
        break;
      case 'cena_zakupu':
        valueA = parseFloat(a.cena_zakupu_netto || a.purchase_price || 0);
        valueB = parseFloat(b.cena_zakupu_netto || b.purchase_price || 0);
        break;
      case 'vat':
        valueA = parseFloat(a.stawka_vat || 0);
        valueB = parseFloat(b.stawka_vat || 0);
        break;
      case 'stan':
        valueA = parseFloat(a.stock_quantity || a.stan_magazynowy || 0);
        valueB = parseFloat(b.stock_quantity || b.stan_magazynowy || 0);
        break;
      case 'marza':
        valueA = productMargins[a.id]?.margin_percent || 0;
        valueB = productMargins[b.id]?.margin_percent || 0;
        break;
      default:
        valueA = (a.nazwa || '').toLowerCase();
        valueB = (b.nazwa || '').toLowerCase();
    }
    
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return sortDirection === 'asc' 
        ? valueA.localeCompare(valueB, 'pl') 
        : valueB.localeCompare(valueA, 'pl');
    } else {
      return sortDirection === 'asc' 
        ? valueA - valueB 
        : valueB - valueA;
    }
  });

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Ładowanie...</span>
        </div>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="text-center py-4">
        <div className="text-muted">
          <i className="fas fa-box-open fa-3x mb-3"></i>
          <h5>Brak produktów</h5>
          <p>Nie znaleziono produktów spełniających kryteria wyszukiwania</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100%', 
      backgroundColor: 'var(--card-bg)',
      border: '1px solid var(--border)',
      borderRadius: '0.375rem',
      boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)',
      overflowX: 'auto'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        padding: '0.75rem 1rem',
        borderBottom: '1px solid var(--border)'
      }}>
        <h6 style={{ 
          margin: 0, 
          color: 'var(--text-secondary)', 
          fontWeight: '600',
          fontSize: '0.9rem'
        }}>
          <i className="fas fa-table me-2" style={{ color: 'var(--text-muted)' }}></i>
          Lista produktów
        </h6>
        <div style={{ 
          fontSize: '0.75rem', 
          color: 'var(--text-muted)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <i className="fas fa-list"></i>
          <span>Produkty: <strong>{products.length}</strong></span>
        </div>
      </div>
      
      <div style={{ overflowX: 'auto' }}>
        <table style={{ 
          width: '100%',
          minWidth: '850px',
          fontSize: '0.8rem',
          borderCollapse: 'separate',
          borderSpacing: 0
        }}>
          <thead style={{ 
            backgroundColor: 'var(--bg)',
            borderBottom: '2px solid var(--border)'
          }}>
            <tr style={{ fontSize: '0.75rem' }}>
              <th style={{
                width: '35px',
                padding: '0.5rem 0.25rem',
                textAlign: 'center',
                fontWeight: '600',
                color: 'var(--text-secondary)'
              }}>Lp</th>
              <th onClick={() => handleSort('id')} style={{
                width: '40px',
                padding: '0.5rem 0.25rem',
                textAlign: 'center',
                fontWeight: '600',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                userSelect: 'none'
              }}>ID <SortIcon column="id" /></th>
              <th onClick={() => handleSort('nazwa')} style={{ 
                minWidth: '250px', 
                width: '28%', 
                padding: '0.5rem 0.5rem',
                fontWeight: '600',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                userSelect: 'none'
              }}>Produkt <SortIcon column="nazwa" /></th>
              <th onClick={() => handleSort('kategoria')} style={{ 
                width: '80px', 
                padding: '0.5rem 0.25rem',
                textAlign: 'center',
                fontWeight: '600',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                userSelect: 'none'
              }}>Kategoria <SortIcon column="kategoria" /></th>
              <th onClick={() => handleSort('cena_sprzedazy')} style={{ 
                width: '75px', 
                padding: '0.5rem 0.25rem',
                textAlign: 'center',
                fontWeight: '600',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                userSelect: 'none'
              }}>Cena sprz. <SortIcon column="cena_sprzedazy" /></th>
              <th onClick={() => handleSort('cena_zakupu')} style={{ 
                width: '75px', 
                padding: '0.5rem 0.25rem',
                textAlign: 'center',
                fontWeight: '600',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                userSelect: 'none'
              }}>Cena zak. <SortIcon column="cena_zakupu" /></th>
              <th onClick={() => handleSort('vat')} style={{ 
                width: '45px', 
                padding: '0.5rem 0.25rem',
                textAlign: 'center',
                fontWeight: '600',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                userSelect: 'none'
              }}>VAT <SortIcon column="vat" /></th>
              <th onClick={() => handleSort('stan')} style={{ 
                width: '55px', 
                padding: '0.5rem 0.25rem',
                textAlign: 'center',
                fontWeight: '600',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                userSelect: 'none'
              }}>Stan <SortIcon column="stan" /></th>
              <th style={{ 
                width: '40px', 
                padding: '0.5rem 0.25rem',
                textAlign: 'center',
                fontWeight: '600',
                color: 'var(--text-secondary)'
              }}>Jedn.</th>
              <th onClick={() => handleSort('marza')} style={{ 
                width: '80px', 
                padding: '0.5rem 0.25rem',
                textAlign: 'center',
                fontWeight: '600',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                userSelect: 'none'
              }}>Marża <SortIcon column="marza" /></th>
              <th style={{ 
                width: '70px', 
                padding: '0.5rem 0.25rem',
                textAlign: 'center',
                fontWeight: '600',
                color: 'var(--text-secondary)'
              }}>Akcje</th>
            </tr>
          </thead>
          <tbody>
        {sortedProducts.map((product, index) => (
          <React.Fragment key={product.id}>
            <tr style={{
              backgroundColor: index % 2 === 0 ? 'var(--bg)' : 'var(--card-bg)' ,
              borderBottom: '1px solid var(--border)'
            }}>
              <td style={{
                padding: '0.5rem 0.25rem',
                textAlign: 'center',
                color: 'var(--text-muted)',
                fontSize: '0.7rem',
                fontWeight: '500'
              }}>
                {index + 1}
              </td>
              <td style={{
                padding: '0.5rem 0.25rem',
                textAlign: 'center'
              }}>
                <span style={{
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.65rem',
                  border: '1px solid var(--info)', color: 'var(--info)' ,
                  borderRadius: '0.25rem',
                  fontWeight: '600'
                }}>
                  #{product.id}
                </span>
              </td>
              <td style={{ 
                minWidth: '250px', 
                width: '28%', 
                padding: '0.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      fontSize: '0.85rem', 
                      lineHeight: '1.2',
                      fontWeight: '600',
                      color: 'var(--text)',
                      marginBottom: '0.25rem',
                      wordWrap: 'break-word',
                      whiteSpace: 'normal',
                      overflow: 'visible'
                    }}>
                      {product.name}
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '0.25rem',
                      marginTop: '0.25rem'
                    }}>
                      {product.barcode && product.barcode !== 'null' && product.barcode !== 'None' && (
                        <span style={{ 
                          padding: '0.125rem 0.375rem',
                          fontSize: '0.65rem',
                          border: '1px solid var(--info)', color: 'var(--info)' ,
                          borderRadius: '0.25rem'
                        }}>
                          <i className="fas fa-barcode me-1"></i>
                          EAN: {product.barcode}
                          {product.ean_count > 1 && (
                            <span style={{
                              marginLeft: '0.25rem',
                              padding: '0 0.25rem',
                              backgroundColor: 'rgba(255,255,255,0.3)',
                              borderRadius: '0.2rem',
                              fontWeight: 'bold'
                            }}>
                              +{product.ean_count - 1}
                            </span>
                          )}
                        </span>
                      )}
                      {product.product_code && product.product_code !== 'null' && product.product_code !== 'None' && (
                        <span style={{ 
                          padding: '0.125rem 0.375rem',
                          fontSize: '0.65rem',
                          border: '1px solid var(--text-muted)', color: 'var(--text-muted)' ,
                          borderRadius: '0.25rem'
                        }}>
                          <i className="fas fa-qrcode me-1"></i>
                          Kod: {product.product_code}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </td>
              <td style={{ 
                padding: '0.5rem 0.25rem',
                textAlign: 'center'
              }}>
                <span style={{ 
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.7rem',
                  backgroundColor: 'var(--bg)',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: '0.25rem'
                }}>
                  {product.category_name || product.category || 'Bez kategorii'}
                </span>
              </td>
              <td style={{ 
                padding: '0.5rem 0.25rem',
                textAlign: 'center'
              }}>
                <div style={{ 
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  color: 'var(--success)'
                }}>
                  {parseFloat(product.cena_sprzedazy_brutto || product.price || 0).toFixed(2)} zł
                </div>
              </td>
              <td style={{ 
                padding: '0.5rem 0.25rem',
                textAlign: 'center'
              }}>
                <div style={{ 
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  color: 'var(--info)'
                }}>
                  {parseFloat(product.cena_zakupu_brutto || product.purchase_price || 0).toFixed(2)} zł
                </div>
              </td>
              <td style={{ 
                padding: '0.5rem 0.25rem',
                textAlign: 'center'
              }}>
                <span style={{
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.7rem',
                  backgroundColor: product.stawka_vat === -2 ? 'var(--text-muted)' : product.stawka_vat === -1 ? 'var(--info)' : 'var(--warning)',
                  color: product.stawka_vat === -2 || product.stawka_vat === -1 ? '#fff' : 'var(--text)',
                  borderRadius: '0.25rem',
                  fontWeight: '600'
                }}>
                  {product.stawka_vat === -2 ? 'NP' : product.stawka_vat === -1 ? 'ZW' : `${product.stawka_vat || 23}%`}
                </span>
              </td>
              <td style={{ 
                padding: '0.5rem 0.25rem',
                textAlign: 'center'
              }}>
                {product.typ_produktu === 'usluga' ? (
                  <div style={{
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: 'var(--purple)',
                    backgroundColor: 'var(--purple-light)',
                    padding: '0.15rem 0.4rem',
                    borderRadius: '0.25rem',
                    display: 'inline-block'
                  }}>
                    ND
                  </div>
                ) : editingStock[product.id] ? (
                  <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                    <input
                      type="number"
                      value={tempValues[product.id] || ''}
                      onChange={(e) => setTempValues(prev => ({ ...prev, [product.id]: e.target.value }))}
                      style={{
                        width: '50px',
                        padding: '0.25rem',
                        fontSize: '0.7rem',
                        border: '1px solid var(--border)',
                        borderRadius: '0.25rem'
                      }}
                    />
                    <button
                      onClick={() => handleStockSave(product.id)}
                      style={{
                        padding: '4px 10px',
                        fontSize: '11px',
                        border: '1px solid var(--success)', color: 'var(--success)'
                      }}
                    >
                      <i className="fas fa-check"></i>
                    </button>
                    <button
                      onClick={() => handleStockCancel(product.id)}
                      style={{
                        padding: '4px 10px',
                        fontSize: '11px',
                        border: '1px solid var(--danger)', color: 'var(--danger)'
                      }}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ) : (
                  <div style={{
                    fontSize: '0.8rem',
                    fontWeight: '700',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer'
                  }} onClick={() => handleStockEdit(product.id, product.stock_quantity)}>
                    {product.stock_quantity || 0}
                  </div>
                )}
              </td>
              <td style={{ 
                padding: '0.5rem 0.25rem',
                textAlign: 'center'
              }}>
                <span style={{ 
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.7rem',
                  backgroundColor: 'var(--bg)',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: '0.25rem'
                }}>
                  {product.unit || 'szt'}
                </span>
              </td>
              <td style={{ 
                padding: '0.5rem 0.25rem',
                textAlign: 'center'
              }}>
                {(() => {
                  // Pobierz marżę z centralnego API
                  const margin = productMargins[product.id];
                  
                  if (margin && (margin.margin_percent > 0 || margin.margin_amount > 0)) {
                    return (
                      <div style={{ display: 'flex', flexDirection: 'row', gap: '0.25rem', justifyContent: 'center', alignItems: 'center' }}>
                        <span style={{ 
                          padding: '0.125rem 0.375rem',
                          fontSize: '0.65rem',
                          backgroundColor: margin.margin_percent > 20 ? 'var(--success)' : margin.margin_percent > 10 ? 'var(--warning)' : 'var(--danger)',
                          color: margin.margin_percent > 10 ? '#fff' : 'var(--text)',
                          borderRadius: '0.25rem',
                          fontWeight: '600',
                          border: getMarginCalculationData(product).hasSpecialPrice ? '2px solid var(--info)' : 'none'
                        }}>
                          {margin.margin_percent.toFixed(1)}%
                        </span>
                        <span style={{ 
                          padding: '0.125rem 0.375rem',
                          fontSize: '0.6rem',
                          backgroundColor: getMarginCalculationData(product).hasSpecialPrice ? 'var(--info-light)' : 'var(--bg)',
                          color: 'var(--text-secondary)',
                          border: '1px solid var(--border)',
                          borderRadius: '0.25rem'
                        }}>
                          {margin.margin_amount.toFixed(2)}zł
                        </span>
                        {getMarginCalculationData(product).hasSpecialPrice && (
                          <span style={{ 
                            fontSize: '0.5rem',
                            color: 'var(--info)',
                            fontWeight: 'bold'
                          }}>
                            ⭐
                          </span>
                        )}
                      </div>
                    );
                  } else {
                    return (
                      <span style={{ 
                        fontSize: '0.65rem',
                        color: 'var(--text-muted)'
                      }}>
                        -
                      </span>
                    );
                  }
                })()}
              </td>
              <td style={{ 
                padding: '0.5rem 0.25rem',
                textAlign: 'center'
              }}>
                {editingProduct && editingProduct.id === product.id ? (
                  <span style={{ 
                    fontSize: '0.7rem',
                    color: 'var(--text-muted)'
                  }}>
                    Edytuje...
                  </span>
                ) : (
                  <ActionMenu actions={[
                    canEdit && { icon: '✏️', label: 'Zmień', color: 'var(--info)', onClick: () => handleEditProduct(product) },
                    onShowHistory && { icon: '📊', label: 'Historia', color: 'var(--cyan)', onClick: () => onShowHistory(product) },
                  ]} />
                )}
              </td>
            </tr>
            
            {/* Formularz edycji bezpośrednio pod edytowanym wierszem */}
            {editingProduct && editingProduct.id === product.id && (
              <tr>
                <td colSpan="11" style={{ padding: '0', border: 'none' }}>
                  <div style={{ 
                    background: 'var(--bg)', 
                    border: '2px solid var(--info)',
                    borderRadius: '8px',
                    margin: '4px',
                    padding: '0'
                  }}>
                    <InlineProductEdit
                      product={editingProduct}
                      onSave={handleSaveProductEdit}
                      onCancel={handleCancelEdit}
                    />
                  </div>
                </td>
              </tr>
            )}
            
          </React.Fragment>
        ))}
      </tbody>
        </table>
      </div>
      
      {/* Paginacja */}
      {pagination && pagination.pages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '20px',
          borderTop: '1px solid var(--border)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <button
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                border: '1px solid var(--border)',
                backgroundColor: pagination.page <= 1 ? 'var(--bg)' : 'var(--card-bg)',
                color: pagination.page <= 1 ? 'var(--text-muted)' : 'var(--info)'
              }}
            >
              ← Poprzednia
            </button>
            
            <span style={{
              margin: '0 15px',
              fontSize: '14px',
              color: 'var(--text-muted)'
            }}>
              Strona {pagination.page} z {pagination.pages} ({pagination.total} produktów)
            </span>
            
            <button
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                border: '1px solid var(--border)',
                backgroundColor: pagination.page >= pagination.pages ? 'var(--bg)' : 'var(--card-bg)',
                color: pagination.page >= pagination.pages ? 'var(--text-muted)' : 'var(--info)'
              }}
            >
              Następna →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryTable;
